<template>
    <div class="smsmallar">
        <modal-basic ref="dialog" />
        <div class="header">
            <h1>Sms-mallar</h1>
            <button type="button" @click="openCreateSMSTab">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Skapa sms-mall</span>
            </button>
        </div>

        <search-filter v-model="filterValues" @input="handleSearch" :fields="filterFields">
            <template v-slot:header-filters>
                <KText class="search-field" name="q" placeholder="Sök" />
            </template>
            <template v-slot:fields>
                <KText type="text" name="namn__icontains" label="Mall namn" />
                <KText type="text" name="typ" label="Typ av sms-mall " />
                <KAutocomplete
                    ref="profilesAutocomplete"
                    class="autocomplete"
                    name="created_by"
                    label="Skapad av"
                    :onSearch="searchProfiles"
                    :optionMapper="optionMapper"
                    :options="createdBy"
                    :minQuery="3"
                />
            </template>
        </search-filter>

        <kliniken-table
            :items="items"
            :columns="columns"
            :count="smsmallarCount"
            :dropdown-actions="dropdownActions"
            :has-dropdown="hasDropdown"
            @sort="sort"
            @redigeraSmsmall="redigeraSmsmall"
            @dupliceraSmsmall="dupliceraSmsmall"
            @raderaSmsmall="raderaSmsmall"
            showGreyBackground
        />
    </div>
</template>

<script lang="ts">
    import tabIcon from "@/assets/blank.png";

    export default {
        name: "SMSMallar",
        tabName: "Sms-mallar",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, ref, toRefs, onBeforeUnmount } from "vue";

    import { useStore } from "@/store";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { flashMessage, openDialog } from "@/utils";
    import tabEvents from "@/tabEvents";
    import SMSMallForm from "@/tabs/Operation/SMS/SMSMallForm.vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import EditIcon from "@/assets/actionIcons/edit.svg";
    import DeleteIcon from "@/assets/actionIcons/delete.svg";
    import DuplicateIcon from "@/assets/actionIcons/duplicate.svg";
    import ModalBasic, { BasicModalVariant } from "@/components/generic/ModalBasic.vue";
    import SearchFilter from "@/components/SearchFilter.vue";

    const props = defineProps<{
        tabId: number;
        parameters?: Record<string, any>;
    }>();
    const { tabId } = toRefs(props);

    const store = useStore();
    const dialog = ref();
    const profilesAutocomplete = ref();

    const filterValues = ref<Record<string, any>>({});
    const sortField = ref("");
    const items = ref([]);
    const hasDropdown = ref(true);
    const smsmallarCount = ref(0);
    const createdBy = ref([]);

    const columns = [
        {
            title: "Mall namn",
            name: "namn",
            sort: "namn",
        },
        {
            title: "Typ av sms-mall",
            name: "typ_display",
            sort: "typ",
        },
        {
            title: "Senast använd",
            name: "updated_at",
            type: "date",
        },
        {
            title: "Skapat av",
            name: "created_by_display.user_display.name",
            sort: "created_by",
        },
    ];

    const dropdownActions = [
        {
            name: "redigeraSmsmall",
            title: "Redigera",
            icon: EditIcon,
            show: () => {
                return true;
            },
        },
        {
            name: "dupliceraSmsmall",
            title: "Duplicera",
            icon: DuplicateIcon,
            show: () => {
                return true;
            },
        },
        {
            name: "raderaSmsmall",
            title: "Radera",
            icon: DeleteIcon,
            show: () => {
                return true;
            },
        },
    ];

    const filterFields = computed(() => ({
        namn__icontains: { label: "Namn " },
        typ: { label: "Typ " },
        created_by: { label: "Skapat av ", options: createdBy.value },
        q: { label: "Sök " },
    }));

    init();

    async function init() {
        await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });
        tabEvents.$on("refreshSmsMallar", _refreshSmsMallar);
        await _fetchSmsMallar();
        await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
    }

    const optionMapper = ({ pk, user }: { pk: number; user: { name: string } }) => ({
        value: pk,
        label: user.name,
    });

    const openCreateSMSTab = () => {
        store.dispatch("tabs/openTab", {
            component: SMSMallForm,
        });
    };

    const redigeraSmsmall = (options: Record<string, any> = {}) => {
        store.dispatch("tabs/openTab", {
            component: SMSMallForm,
            parameters: { ...options, isEditing: true },
        });
    };

    const dupliceraSmsmall = (options: Record<string, any> = {}) => {
        const { id, ...rest } = options;

        store.dispatch("tabs/openTab", {
            component: SMSMallForm,
            parameters: { ...rest, isDuplicating: true },
        });
    };

    const raderaSmsmall = async ({ id }: { id: number }) => {
        if (id) {
            const confirm = await dialog.value.show({
                variant: BasicModalVariant.WARNING,
                title: "Vill du verkligen radera hela sms-mallen?",
                okButton: "Ja, radera",
                cancelButton: "Nej, avbryt",
            });

            if (confirm) {
                await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });
                try {
                    const response = await klinikenApi.delete(`/sms/mallar/${id}/`, getConfig());
                    if (response.status === 204) {
                        flashMessage("Sms-mall raderad");
                        await _fetchSmsMallar();
                    }
                } catch (error) {
                    openDialog(getErrorMessage(error), "error");
                } finally {
                    await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
                }
            }
        }
    };

    const sort = async (field: string) => {
        sortField.value = field;
        await _fetchSmsMallar();
    };

    const handleSearch = async (filterData: Record<string, any>) => {
        filterValues.value = filterData;
        await _fetchSmsMallar();
    };

    const searchProfiles = async ({ query }: { query?: string } = {}) => {
        try {
            const response = await klinikenApi.get(
                "/core/profiles/",
                getConfig({
                    params: { q: query, is_active: true },
                })
            );

            const convertedOptions = response?.data?.results.map(optionMapper) || [];
            createdBy.value = convertedOptions;

            profilesAutocomplete.value?.setOptions({ options: convertedOptions });

            return response?.data?.results;
        } catch (error) {
            console.error(error);
        }
    };

    const _fetchSmsMallar = async () => {
        let params = {
            ...filterValues.value,
            ...{
                ordering: sortField.value,
            },
        };

        try {
            const response = await klinikenApi.get("/sms/mallar/", getConfig({ params }));
            const { results, count } = response?.data || {};

            items.value = results || [];
            smsmallarCount.value = count;
        } catch (error: any) {
            throw new Error(error);
        }
    };

    const _refreshSmsMallar = () => {
        _fetchSmsMallar();
    };

    onBeforeUnmount(() => {
        tabEvents.$off("refreshSmsMallar", _refreshSmsMallar);
    });
</script>

<style lang="scss" scoped>
    .smsmallar {
        .header {
            display: flex;
            align-items: center;
            gap: 40px;
            margin-bottom: 26px;

            h1 {
                margin: 0 !important;
                font-family: Roboto;
                font-style: normal;
                font-weight: 400;
                font-size: 30px;
                line-height: 34px;
                color: #277692;
            }

            .button-hollow:hover {
                background-color: transparent;
            }

            button {
                background: transparent;
                border: none;
                display: flex;
                align-items: center;
                justify-content: center;

                img {
                    margin-right: 8px;
                }

                span {
                    color: #6c8188;
                    font-family: "Roboto Medium", serif;
                    font-size: 16px;
                    line-height: 0;
                    white-space: nowrap;
                }
            }
        }

        .search-field {
            width: calc(256px * 1.5);
        }
    }
</style>
